/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Product Offerings Reducer
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.10.0
 */

import produce from 'immer';
import { Reducer } from 'redux';

import { IAuthRegion } from '../PageAuth/types/authTypes';
import { IMegaPower } from '../PageSubscriberId/components/Info/types';
import {
    PRODUCT_OFFERING_CLEAR,
    PRODUCT_OFFERING_SET_MEGAPOWER,
    PRODUCT_OFFERING_SET_PARAMS,
    PRODUCT_OFFERING_SET_STEP_VALID,
} from './wizardProductOfferingsConstants';
import {
    IMegaPowersTotal,
    IProductOfferingsListItem,
    IProductOfferingsSubscriberActivationItem,
    IRatePlan,
    IService,
    TProductOfferingsActions,
} from './wizardProductOfferingsTypes';

export interface IWizardProductOfferingsState {
    dateFrom: string;
    dateTo: string;
    timeFrom: string;
    timeTo: string;
    isDisconnect: boolean;
    isStepValid: boolean;
    isTariff: boolean;
    maxSubsForProductActivation: number;
    productOfferings: IProductOfferingsListItem[];
    initialMegaPowers: IMegaPower[];
    totalMegaPowers: IMegaPowersTotal;
    megaPowers: IMegaPower[];
    // при старте визарда всегда указывается id региона абонента
    regionId: number;
    // если у абонента доступно несколько регионов, то выбирается region на основе regionId
    region?: IAuthRegion;
    removedSubscribers: string[];
    subsCount: number;
    subscriberActivationList: IProductOfferingsSubscriberActivationItem[];
    totalPrice: number;
    validateError: string;
    ratePlan?: IRatePlan;
    services?: IService[];
}

export const initWizardProductOfferingsState: IWizardProductOfferingsState = {
    dateFrom: '',
    dateTo: '',
    timeFrom: '',
    timeTo: '',
    isDisconnect: false,
    isStepValid: true,
    isTariff: false,
    maxSubsForProductActivation: 200,
    productOfferings: [],
    initialMegaPowers: [],
    totalMegaPowers: {},
    megaPowers: [],
    regionId: 0,
    removedSubscribers: [],
    subsCount: 0,
    subscriberActivationList: [],
    totalPrice: 0,
    validateError: '',
};

export const wizardProductOfferingsReducer: Reducer<IWizardProductOfferingsState, TProductOfferingsActions> = (
    state = initWizardProductOfferingsState,
    action: TProductOfferingsActions,
) =>
    produce(state, (draft) => {
        switch (action.type) {
            case PRODUCT_OFFERING_SET_STEP_VALID:
                draft.isStepValid = action.payload.isStepValid;
                draft.validateError = action.payload.validateError;
                break;
            case PRODUCT_OFFERING_SET_PARAMS:
                Object.assign(draft, { ...draft, ...action.payload });
                break;
            case PRODUCT_OFFERING_SET_MEGAPOWER:
                const findedMegaPower = draft.megaPowers.find((megaPower) => megaPower.id === action.payload.id);
                if (findedMegaPower) {
                    findedMegaPower.connected = !findedMegaPower.connected;
                }
                break;
            case PRODUCT_OFFERING_CLEAR:
                return initWizardProductOfferingsState;
            default:
                break;
        }
    });
